<script setup lang="ts">
import { useCurrency } from "~/composables/currency/useCurrency";

const props = defineProps(["modelValue", "error", "limit"]);
const emit = defineEmits(["update:modelValue"]);
const attrs = useAttrs();
const { currencySymbol } = useCurrency();

const value = computed({
  get() {
    let currentValue = props.modelValue;

    if ((currentValue === null || currentValue === undefined) && Number.isFinite(attrs.value)) {
      currentValue = attrs.value;
    }

    if (limit.value !== null && !isNaN(+currentValue)) {
      const factor = Math.pow(10, limit.value);
      currentValue = Math.round(+currentValue * factor) / factor;
    }

    return currentValue;
  },
  set(value) {
    emit("update:modelValue", handleInput(value));
  },
});

function handleInput(value: string | number): number | null {
  if (value === "") {
    return null;
  } else {
    let numericValue = +value;
    if (limit.value !== null && !isNaN(numericValue)) {
      const factor = Math.pow(10, limit.value);
      numericValue = Math.round(numericValue * factor) / factor;
    }
    return numericValue;
  }
}

const limit = computed<number | null>(() => {
  return props.limit || null;
});
</script>

<template>
  <span class="price_input_container body_regular" :data-error="error">
    <input v-bind="$attrs" v-model="value" /><span
      class="pr-[10px]"
      :class="Number.isFinite(value) ? '' : 'text-medium-grey-color'"
      >{{ currencySymbol }}</span
    >
  </span>
</template>

<style lang="scss">
@use "assets/scss/texts";
@use "assets/scss/common";

.price_input_container {
  @extend .heading_4_regular;
  @apply flex flex-1 items-center;
  background: var(--grey-light);
  border: 1px solid var(--grey-lightest);
  border-radius: 8px;
  height: 40px;
  color: var(--text-main-color);

  /* Adjust border on hover or focus of input */
  &:has(input:not([disabled]):not([readonly]):hover),
  &:has(input:not([disabled]):not([readonly]):focus) {
    border-color: var(--primary-blue-color);
  }

  /* Placeholder styling remains unchanged */
  span {
    &:disabled,
    &:read-only {
      color: var(--text-secondary-color);
    }
  }
  input {
    @apply text-primary-color w-full text-end px-[4px];
    background: none;
    line-height: 20px;
    height: 100%;

    &::placeholder {
      color: var(--grey-medium);
    }

    &:disabled,
    &:read-only {
      color: var(--text-secondary-color);
      border-color: transparent;
    }
  }

  /* Remove border when input is disabled or readonly */
  &:has(input:disabled),
  &:has(input:read-only) {
    border-color: transparent;
  }

  /* Error styling */
  &[data-error="true"] {
    border: 1px solid var(--red-main);
  }
}
</style>
